import redirect from 'js/lib/coursera.redirect';
import user from 'js/lib/user';

import { isS12nEligibleForCourseraPlusUpgrade } from 'bundles/coursera-plus-common/utils/courseraPlusUpgradeUtils';
import EnrollmentChoiceTypes from 'bundles/enroll-common/common/EnrollmentChoiceTypes';
import {
  choiceTypeToHandleSubmitPromise,
  submitEnrollmentPromise,
} from 'bundles/enroll-common/lib/enrollmentChoiceUtils';
import {
  getCanEnrollThroughS12nSubscription,
  getCanEnrollThroughS12nSubscriptionFreeTrial,
  getS12nMonthlySkuId,
  getS12nSubscriptionHasExhaustedFreeTrial,
} from 'bundles/enroll-common/utils/mixAndMatchUtils';
import usePageData from 'bundles/enroll-modal/data/usePageData';
import { useEnrollModalEventing } from 'bundles/enroll-modal/utils/eventingUtils';
import adsTracker from 'bundles/event-tracking/lib';

export const useEnrollInS12nSubscription = (
  isFromS12nSelection: boolean = false,
  isUsingPrePurchaseUpsellFlow: boolean = false,
  isPrePurchaseUpsellAllPlansShown: boolean = false
) => {
  const { trackEnrollModalContinue } = useEnrollModalEventing({
    isUsingPrePurchaseUpsellFlow,
    isPrePurchaseUpsellAllPlansShown,
  });
  const {
    enrollmentAvailableChoices,
    s12n,
    course,
    promotionEligibilities,
    isGuestCheckout,
    s12nSubscriptionOwnableProduct,
  } = usePageData();
  const s12nId = s12n?.id;
  const courseId = course.id;
  const enrollmentData = s12nId ? { enrollmentAvailableChoices, isFromS12nSelection, s12nId } : undefined;
  const canEnrollThroughS12nSubscription = enrollmentData ? getCanEnrollThroughS12nSubscription(enrollmentData) : false;
  const canEnrollThroughS12nSubscriptionFreeTrial = enrollmentData
    ? getCanEnrollThroughS12nSubscriptionFreeTrial(enrollmentData)
    : false;
  const hasExhaustedFreeTrial = enrollmentData ? getS12nSubscriptionHasExhaustedFreeTrial(enrollmentData) : undefined;
  let s12nMonthlySkuId: string | undefined;
  if (isGuestCheckout) {
    s12nMonthlySkuId = s12nSubscriptionOwnableProduct?.productItemId;
  } else if (enrollmentData) {
    s12nMonthlySkuId = getS12nMonthlySkuId(enrollmentData);
  }
  const promoCode = promotionEligibilities?.isEligible ? promotionEligibilities?.promoCodeId : undefined;

  const enrollInSubscription = async (additionalParams?: Record<string, string>) => {
    const enrollmentChoiceType = EnrollmentChoiceTypes.ENROLL_THROUGH_S12N_SUBSCRIPTION;

    // Track S12nSubscriptionEnrollButtonClick GTM event
    adsTracker.trackS12nEnrollModalButtonClick({ userId: user.get()?.id, productId: s12nId });

    trackEnrollModalContinue(enrollmentChoiceType);

    if (isGuestCheckout) {
      if (!s12nMonthlySkuId || !s12nId) {
        return Promise.resolve({});
      }

      const checkoutQuery = new URLSearchParams();
      checkoutQuery.set('productType', 'SpecializationSubscription');
      checkoutQuery.set('productItemId', s12nMonthlySkuId);
      checkoutQuery.set('s12nId', s12nId);

      if (courseId) {
        checkoutQuery.set('courseId', courseId);
      }
      if (promoCode) {
        checkoutQuery.set('promoCode', promoCode);
      }

      checkoutQuery.set('returnTo', `${window.location.pathname}${window.location.search}`);
      redirect.setLocation(`/payments/checkout?${checkoutQuery.toString()}`);
      return Promise.resolve({});
    }

    // Skip the s12n welcome email if user can enroll in free trial, user is in Plus upsell epic,
    // and the s12n is eligible for the Plus upgrade
    let skipWelcomeEmail = false;
    if (s12nId && canEnrollThroughS12nSubscriptionFreeTrial) {
      skipWelcomeEmail = await isS12nEligibleForCourseraPlusUpgrade(s12nId);
    }

    const handleSubmitPromise = choiceTypeToHandleSubmitPromise[enrollmentChoiceType];
    const options = { productSkuId: s12nMonthlySkuId, courseId, skipWelcomeEmail };

    return submitEnrollmentPromise({
      handleSubmitPromise,
      options,
      promoCode,
      additionalParams,
    });
  };

  return {
    enrollInSubscription,
    s12nMonthlySkuId,
    canEnrollThroughS12nSubscription,
    canEnrollThroughS12nSubscriptionFreeTrial,
    hasExhaustedFreeTrial,
  };
};
